import { EllipsisVerticalIcon, LogOutIcon, SettingsIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { usePreferencesSheet } from "@/features/preferences/preferences-sheet"

/**
 * The profile menu — two items and a heading (§11.1).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT USED TO HOLD THREE NESTED SUBMENUS, and they are gone for two reasons.
 *
 * ERGONOMICS. A submenu inside a popup is a small target inside a scrolling
 * surface, on a phone, for a man in his fifties in a workshop. §11.1 sets a 48px
 * floor on controls; nested menu rows are nowhere near it, and hovering to open
 * one does not exist as a gesture on touch.
 *
 * ROOM TO GROW. Changing the PIN, changing the display name and showing the
 * email all need endpoints that do not exist yet (`/auth/*` is owner-login,
 * admin-login and refresh). When they land they need somewhere to live, and it
 * is not a third level of dropdown.
 *
 * A NOTE ON WHAT CRASHED HERE. The header block below is a plain `div`, not
 * `DropdownMenuLabel`. This shadcn build is Base UI, where that component maps
 * to `Menu.GroupLabel` and THROWS unless wrapped in a `Menu.Group` —
 * "MenuGroupContext is missing". Radix, which most shadcn examples target,
 * tolerates a bare label. Here it was an uncaught render error that blanked the
 * entire page: tapping your own name signed you out of the interface.
 * `nav-user.test.tsx` opens this menu and fails on that console error.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function NavUser({
  user,
  onSignOut,
}: {
  user: {
    name: string
    email: string
    avatar: string
  }
  onSignOut: () => void
}) {
  const { t } = useTranslation()
  const { isMobile } = useSidebar()
  /*
   * THE SHEET IS NOT OWNED HERE ANY MORE — see the note at the bottom of this
   * component and `features/preferences/preferences-sheet.tsx`. It is still
   * hoisted above the menu, just one level further out, so the search palette's
   * "My settings" row can open the same one.
   */
  const preferences = usePreferencesSheet()

  /* First character of the display name — "S" for Shubham, "श" for a Marathi
   * name. Never a hardcoded letter, and never an image we do not have. */
  const initial = [...user.name][0]?.toUpperCase() ?? ""

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={<SidebarMenuButton size="lg" className="aria-expanded:bg-muted" />}
          >
            <Avatar className="size-8 rounded-lg grayscale">
              <AvatarImage src={user.avatar} alt="" />
              <AvatarFallback className="rounded-lg">{initial}</AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{user.name}</span>
              <span className="text-foreground/70 truncate text-xs">{user.email}</span>
            </div>
            <EllipsisVerticalIcon className="ml-auto size-4" />
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="min-w-56"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            {/* A plain div — see the note in this file's header. */}
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="size-8 rounded-lg grayscale">
                <AvatarImage src={user.avatar} alt="" />
                <AvatarFallback className="rounded-lg">{initial}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user.name}</span>
                <span className="text-muted-foreground truncate text-xs">{user.email}</span>
              </div>
            </div>

            <DropdownMenuSeparator />

            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => {
                  preferences.open()
                }}
              >
                <SettingsIcon />
                {t("pref.title")}
              </DropdownMenuItem>
            </DropdownMenuGroup>

            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={onSignOut}>
              <LogOutIcon />
              {t("btn.signOut")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/*
          * THE SHEET IS NOT RENDERED HERE, and that is the same trick one level
          * further out.
          *
          * The obvious shape is a `SheetTrigger` wrapping the menu item, the way
          * shadcn's own demo wires it. It does not work: clicking a menu item
          * CLOSES the menu, and `DropdownMenuContent` unmounts its children when
          * it closes — so the sheet would be torn down in the same frame that
          * opened it and flash out of existence. The open state has to live
          * somewhere that outlives the menu.
          *
          * It used to live in this component. It now lives in
          * `PreferencesSheetProvider`, mounted by `AppShell`, because the search
          * palette needs to open the SAME sheet — `routes.profile` was never a
          * route, so the palette's "My settings" row navigated into the
          * catch-all and landed on the dashboard. One sheet, several triggers.
          */}
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
